import { ISelectItem } from "./types";

export const SPECTRUM_API_KEY = '1b4571a0-f9b3-484b-929c-ea85ca66d0ef';

export const ROUTES = {
    SignIn: '/signin',
    SignInSimplyPerio: '/guidance/perio',
    SignInSimplyImplantitis: '/guidance/implantitis',
    ResetPassword: '/resetpassword',
    ResetPasswordChange: '/resetpassword/:userId/change/:token',
    Dashboard: '/',
    DocDisplay: '/guidance/perio/pages',
    DocDisplayImplantitis: '/guidance/implantitis/pages',
    Orders: '/orders',
    KitOrders: '/supply-history',
    PrefilterOrders: '/orders/search/pages',
    NewReportOrders: '/orders/search/pages/NewReportOrdersSearch',
    CreateOrder: '/orders/new',
    EditOrder: '/orders/:orderId',
    UserGroups: '/userGroups',
    CreateUserGroup: '/userGroups/new',
    EditUserGroup: '/userGroups/:userGroupId',
    Users: '/users',
    EditUser: '/users/:userId',
    CreateUser: '/users/new',
    Organizations: '/organizations',
    CreateOrganization: '/organizations/new',
    EditOrganization: '/organizations/:orgId',
    CreateOrganizationUser: '/organizations/:orgId/users/new',
    EditOrganizationUser: '/organizations/:orgId/users/:userId',
    EditOrganizationBilling: '/organizationbilling/:orgId',
    // :orgId optional - falls back to the logged-in user's own org when omitted (e.g. reached
    // from the Dashboard's own billing card). Ship to Clinic's "Change" link passes the actual
    // org selected there, since that can be a different org than the logged-in user's own
    // (Portal Admin ordering on behalf of a clinic) - see StripeSetupPage.tsx.
    AddStripePaymentMethod: '/organizationstripebilling/add-edit/pages/StripeSetupPage/:orgId?',
    StripeReturnPage: '/StripeReturnPage',
    OrgBillingReports: '/organizationbilling/search/pages/OrgBillingReportsSearch',
    OrgActivityReports: '/organizationbilling/search/pages/OrgActivityReportsSearch',
    Supplies: '/supplies',
    Kits: '/testing-inventory',
    KitOrderShipToClinic: '/testing-inventory/new',
    ShipToPatient: '/orders/new/patient',
    ClinicSubmission: '/orders/new/clinic',
    PayLink: '/paylink/:token',
    PurchaseHistory: '/purchase-history',
    Support: '/support',
    Account: '/account',
    PlatformMaintenance: '/platform-maintenance',
    AnalyticsDataReference: '/platform-maintenance/analytics-data-reference',
    RevenueAnalyticsDataReference: '/platform-maintenance/revenue-analytics-data-reference',
    TrackingMaintenance: '/tracking-maintenance',
    // Self-service, no :id - the caller's own Enterprise is always resolved server-side.
    EnterpriseStripeSetup: '/enterprise/stripe-setup',
    EnterpriseStripeReturn: '/enterprise/stripe-return',
    SignOut: '/signout',
    SignOutSimplyImplantitis: '/implantitis/signout',
};

export const EXTERNAL_ROUTES = {
    ORDER_SUPPLIES: 'https://simplytest.solutions/testing/order-testing-form/',
    REQUEST_SUPPORT: 'https://simplytest.solutions/contact-us/#support',
};

export const USER_ROLES = {
    Admin: 'Admin',
    User: 'User',
    Provider: 'Provider',
    UserGroupAdmin: 'User Group Admin',
    UserGroupManager: 'User Group Manager',
    PortalAdmin: 'Portal Admin',
    PortalManager: 'Portal Manager',
    OrganizationAdmin: 'Organization Admin',
    OrganizationAdminProvider: 'Organization Admin+Provider',
};

export const USER_ROLES_AVAILABLE = {
    [USER_ROLES.User]: [USER_ROLES.PortalAdmin, USER_ROLES.PortalManager, USER_ROLES.UserGroupManager, USER_ROLES.UserGroupAdmin, USER_ROLES.Admin, USER_ROLES.Provider, USER_ROLES.OrganizationAdmin,USER_ROLES.OrganizationAdminProvider],
    [USER_ROLES.Provider]: [USER_ROLES.OrganizationAdminProvider, USER_ROLES.PortalAdmin, USER_ROLES.PortalManager, USER_ROLES.UserGroupManager, USER_ROLES.UserGroupAdmin, USER_ROLES.Admin, USER_ROLES.OrganizationAdmin],
    [USER_ROLES.Admin]: [USER_ROLES.PortalAdmin, USER_ROLES.PortalManager, USER_ROLES.UserGroupManager, USER_ROLES.UserGroupAdmin, USER_ROLES.OrganizationAdmin],
    [USER_ROLES.OrganizationAdminProvider]: [USER_ROLES.PortalAdmin, USER_ROLES.PortalManager, USER_ROLES.UserGroupManager, USER_ROLES.UserGroupAdmin, USER_ROLES.Admin],
    [USER_ROLES.UserGroupManager]: [USER_ROLES.PortalAdmin, USER_ROLES.PortalManager, USER_ROLES.UserGroupAdmin, USER_ROLES.OrganizationAdmin],
    [USER_ROLES.UserGroupAdmin]: [USER_ROLES.PortalAdmin, USER_ROLES.PortalManager],
    [USER_ROLES.PortalManager]: [USER_ROLES.PortalAdmin],
    [USER_ROLES.PortalAdmin]: [],
};

export const USER_GROUP_ACCESS_ROLES = [
    USER_ROLES.PortalAdmin,
];

export const ORGANIZATIONS_ACCESS_ROLES = [
    USER_ROLES.Admin,
    USER_ROLES.PortalAdmin,
    USER_ROLES.PortalManager,
    USER_ROLES.UserGroupAdmin,
    USER_ROLES.UserGroupManager,
    USER_ROLES.OrganizationAdmin,
    USER_ROLES.OrganizationAdminProvider,
];

export const USERS_ACCESS_ROLES = [
    USER_ROLES.Provider,
    USER_ROLES.User,
];

export const USER_ROLES_HIDE_PHI = [
    USER_ROLES.UserGroupManager,
    USER_ROLES.PortalManager,
];

export const USER_ROLES_ADMINS = [
    USER_ROLES.PortalAdmin,
    USER_ROLES.UserGroupAdmin,
    USER_ROLES.Admin,
    USER_ROLES.OrganizationAdmin,
];

// Sep 2026 - Purchase History shows dollar amounts, card last-4 and an organization's whole
// purchase record, so it is staff-only rather than open to every portal user: the admin roles plus
// both manager roles. Plain User and Provider are out.
//
// Deliberately NOT USER_ROLES_ADMINS: that list omits OrganizationAdminProvider, which is held by
// far more real org admins than any other admin role (22 in dev, against 1 for OrganizationAdmin),
// so reusing it would lock out most of this page's intended audience. Kept as its own list rather
// than widening USER_ROLES_ADMINS, which would change behaviour on every screen that uses it.
// Mirrors JwtAppUser.CanViewPurchaseHistory on the server, which is what actually enforces this -
// this list only decides whether the nav item and route are offered.
export const PURCHASE_HISTORY_ACCESS_ROLES = [
    USER_ROLES.Admin,
    USER_ROLES.OrganizationAdmin,
    USER_ROLES.OrganizationAdminProvider,
    USER_ROLES.UserGroupAdmin,
    USER_ROLES.UserGroupManager,
    USER_ROLES.PortalAdmin,
    USER_ROLES.PortalManager,
];

// The roles whose Purchase History scope spans more than one organization, and which therefore get
// the organization picker. Mirrors the server's CanViewAllOrders / CanViewUserGroupOrders: Portal
// roles see every org, User Group roles see their Price Group. Everyone else is pinned to their own
// organization and has nothing to pick between.
export const PURCHASE_HISTORY_ORG_PICKER_ROLES = [
    USER_ROLES.PortalAdmin,
    USER_ROLES.PortalManager,
    USER_ROLES.UserGroupAdmin,
    USER_ROLES.UserGroupManager,
];

export const USER_ROLES_PORTAL_ADMINS = [
    USER_ROLES.PortalAdmin,
];

export const USER_ROLES_ORGANIZATION_BILLING_ADMIN = [
   USER_ROLES.OrganizationAdmin,
];

export const TEXT = {
    Support: {
        Phone: '1-234-567-8910',
    },
    Unauthorized: {
        DefaultMessage: 'You do not have access to this page. Please contact an administrator if this is an error.',
    }
};

export const FILTER_ALL_ITEM: ISelectItem = { label: "ALL", value: "*" };

export const LS_AUTH_KEY = 'auth';
export const APP_TITLE = 'Spectrum SimplyTest';
export const DEFAULT_UNAUTHORIZED_MESSAGE = 'Session Expired';

export const SEX_MALE_LABEL = 'Male';
export const SEX_MALE_VALUE = 'M';
export const SEX_FEMALE_LABEL = 'Female';
export const SEX_FEMALE_VALUE = 'F';
export const SEX_UNKNOWN_LABEL = 'Unknown';
export const SEX_UNKNOWN_VALUE = 'U';

export const NEEDS_CONTACT_PROVIDER = 'P';
export const NEEDS_CONTACT_LAB = 'L';
export const NEEDS_CONTACT_NOTHING = 'N';

export const SEX_LIST: ISelectItem[] = [
    { label: SEX_FEMALE_LABEL, value: SEX_FEMALE_VALUE },
    { label: SEX_MALE_LABEL, value: SEX_MALE_VALUE },
];

export const GRID_STATUS_LIST: ISelectItem[] = [
    { label: "Status - All", value: "*"},
    { label: "Status - Active", value: "true"},
    { label: "Status - Inactive", value: "false"},
]

export const ORDER_TEST_STI_BLOOD_LABEL = 'Simply STI-Blood';
export const ORDER_TEST_STI_BLOOD_VALUE = 'STI291312001000';

export const ORDER_TEST_STI_BLOOD: ISelectItem = {
    label: ORDER_TEST_STI_BLOOD_LABEL,
    value: ORDER_TEST_STI_BLOOD_VALUE,
};

export const ORDER_TEST_STI_COMPLETE_LABEL = 'Simply STI-Complete';
export const ORDER_TEST_STI_COMPLETE_VALUE = 'STI101112001000';

export const ORDER_TEST_STI_COMPLETE: ISelectItem = {
    label: ORDER_TEST_STI_COMPLETE_LABEL,
    value: ORDER_TEST_STI_COMPLETE_VALUE,
};


export const ORDER_TEST_STI_ORAL_LABEL = 'Simply STI-Oral';
export const ORDER_TEST_STI_ORAL_VALUE = 'STI101212001000';

export const ORDER_TEST_STI_ORAL: ISelectItem = {
    label: ORDER_TEST_STI_ORAL_LABEL,
    value: ORDER_TEST_STI_ORAL_VALUE,
};

export const ORDER_TEST_STI_PREMIUM_LABEL = 'Simply STI-Premium';
export const ORDER_TEST_STI_PREMIUM_VALUE = 'STI901912001000';

export const ORDER_TEST_STI_PREMIUM: ISelectItem = {
    label: ORDER_TEST_STI_PREMIUM_LABEL,
    value: ORDER_TEST_STI_PREMIUM_VALUE,
};

export const ORDER_TEST_PERIODONTAL_STANDARD_LABEL = 'Simply Periodontal-Standard';
export const ORDER_TEST_PERIODONTAL_STANDARD_VALUE = 'PER101222001000';
export const ORDER_TEST_PERIODONTAL_STANDARD: ISelectItem = {
    label: ORDER_TEST_PERIODONTAL_STANDARD_LABEL,
    value: ORDER_TEST_PERIODONTAL_STANDARD_VALUE,
};

export const ORDER_TEST_PERIODONTAL_PREMIUM_LABEL = 'Simply Periodontal-Premium';
export const ORDER_TEST_PERIODONTAL_PREMIUM_VALUE = 'PER190222001000';

export const ORDER_TEST_PERIODONTAL_PREMIUM: ISelectItem = {
    label: ORDER_TEST_PERIODONTAL_PREMIUM_LABEL,
    value: ORDER_TEST_PERIODONTAL_PREMIUM_VALUE,
};

export const ORDER_TEST_KNO_STI_PREMIUM_LABEL = 'KNO STI-Premium*'
export const ORDER_TEST_KNO_STI_PREMIUM_VALUE = 'STI901912102010'

export const ORDER_TEST_KNO_STI_PREMIUM: ISelectItem = {
    label: ORDER_TEST_KNO_STI_PREMIUM_LABEL,
    value: ORDER_TEST_KNO_STI_PREMIUM_VALUE
};

export const ORDER_TEST_ATLN_STI_BASIC_LABEL = 'ALTN STI-Basic'
export const ORDER_TEST_ATLN_STI_BASIC_VALUE = 'STI902912212020'

export const ORDER_TEST_ATLN_STI_BASIC: ISelectItem = {
    label: ORDER_TEST_ATLN_STI_BASIC_LABEL,
    value: ORDER_TEST_KNO_STI_PREMIUM_VALUE,
};

export const ORDER_TEST_ATLN_STI_COMPREHENSIVE_LABEL = 'ALTN STI-Comprehensive'
export const ORDER_TEST_ATLN_STI_COMPREHENSIVE_VALUE = 'STI901912222020'

export const ORDER_TEST_ATLN_STI_COMPREHENSIVE: ISelectItem = {
    label: ORDER_TEST_ATLN_STI_COMPREHENSIVE_LABEL,
    value: ORDER_TEST_ATLN_STI_COMPREHENSIVE_VALUE,
}

export const ORDER_TEST_ATLN_STI_COMPREHENSIVE_PLUS_LABEL = 'ALTN STI-Comprehensive Plus'
export const ORDER_TEST_ATLN_STI_COMPREHENSIVE_PLUS_VALUE = 'STI901912232020'

export const ORDER_TEST_ATLN_STI_COMPREHENSIVE_PLUS: ISelectItem = {
    label: ORDER_TEST_ATLN_STI_COMPREHENSIVE_PLUS_LABEL,
    value: ORDER_TEST_ATLN_STI_COMPREHENSIVE_PLUS_VALUE,
}

export const ORDER_TEST_ATLN_STI_COMPREHENSIVE_PLATINUM_FEMALE_LABEL = 'ALTN STI-Comprehensive Platinum Female'
export const ORDER_TEST_ATLN_STI_COMPREHENSIVE_PLATINUM_FEMALE_VALUE = 'STI921962252020'
export const ORDER_TEST_ATLN_STI_COMPREHENSIVE_PLATINUM_FEMALE: ISelectItem = {
    label: ORDER_TEST_ATLN_STI_COMPREHENSIVE_PLATINUM_FEMALE_LABEL,
    value: ORDER_TEST_ATLN_STI_COMPREHENSIVE_PLATINUM_FEMALE_VALUE,
};

export const ORDER_TEST_ATLN_STI_ORAL_LABEL = 'ALTN STI-Oral'
export const ORDER_TEST_ATLN_STI_ORAL_VALUE = 'STI102212182020'
export const ORDER_TEST_ATLN_STI_ORAL: ISelectItem = {
    label: ORDER_TEST_ATLN_STI_ORAL_LABEL,
    value: ORDER_TEST_ATLN_STI_ORAL_VALUE
}

export const ORDER_TEST_ATLN_HPV_FEMALE_LABEL = 'ALTN HPV Female';
export const ORDER_TEST_ATLN_HPV_FEMALE_VALUE = 'STI301412172020';

export const ORDER_TEST_ATLN_HPV_FEMALE: ISelectItem = {
    label: ORDER_TEST_ATLN_HPV_FEMALE_LABEL,
    value: ORDER_TEST_ATLN_HPV_FEMALE_VALUE
}

export const ORDER_TEST_LIST: ISelectItem[] = [
    ORDER_TEST_STI_COMPLETE,
    ORDER_TEST_STI_ORAL,
    ORDER_TEST_STI_PREMIUM,
    ORDER_TEST_PERIODONTAL_PREMIUM,
    //ORDER_TEST_KNO_STI_PREMIUM,
    ORDER_TEST_ATLN_STI_BASIC,
    ORDER_TEST_ATLN_STI_COMPREHENSIVE,
    ORDER_TEST_ATLN_STI_COMPREHENSIVE_PLUS,
    ORDER_TEST_ATLN_STI_COMPREHENSIVE_PLATINUM_FEMALE,
    ORDER_TEST_ATLN_STI_ORAL,
    ORDER_TEST_ATLN_HPV_FEMALE,
];

export const SAMPLE_SOURCE_URINE = "Urine";
export const SAMPLE_SOURCE_SALIVA = "Saliva";
export const SAMPLE_SOURCE_URINE_BLOOD = "Urine + Blood";
export const SAMPLE_SOURCE_VAGINAL = 'Vaginal (Evalyn brush)';

export const STATES: ISelectItem[] = [
    { label: "Alabama", value: "AL" },
    { label: "Alaska", value: "AK" },
    { label: "American Samoa", value: "AS" },
    { label: "Arizona", value: "AZ" },
    { label: "Arkansas", value: "AR" },
    { label: "California", value: "CA" },
    { label: "Colorado", value: "CO" },
    { label: "Connecticut", value: "CT" },
    { label: "Delaware", value: "DE" },
    { label: "District of Columbia", value: "DC" },
    { label: "Florida", value: "FL" },
    { label: "Georgia", value: "GA" },
    { label: "Guam", value: "GU" },
    { label: "Hawaii", value: "HI" },
    { label: "Idaho", value: "ID" },
    { label: "Illinois", value: "IL" },
    { label: "Indiana", value: "IN" },
    { label: "Iowa", value: "IA" },
    { label: "Kansas", value: "KS" },
    { label: "Kentucky", value: "KY" },
    { label: "Louisiana", value: "LA" },
    { label: "Maine", value: "ME" },
    { label: "Maryland", value: "MD" },
    { label: "Massachusetts", value: "MA" },
    { label: "Michigan", value: "MI" },
    { label: "Minnesota", value: "MN" },
    { label: "Mississippi", value: "MS" },
    { label: "Missouri", value: "MO" },
    { label: "Montana", value: "MT" },
    { label: "Nebraska", value: "NE" },
    { label: "Nevada", value: "NV" },
    { label: "New Hampshire", value: "NH" },
    { label: "New Jersey", value: "NJ" },
    { label: "New Mexico", value: "NM" },
    { label: "New York", value: "NY" },
    { label: "North Carolina", value: "NC" },
    { label: "North Dakota", value: "ND" },
    { label: "Ohio", value: "OH" },
    { label: "Oklahoma", value: "OK" },
    { label: "Oregon", value: "OR" },
    { label: "Pennsylvania", value: "PA" },
    { label: "Puerto Rico", value: "PR" },
    { label: "Rhode Island", value: "RI" },
    { label: "South Carolina", value: "SC" },
    { label: "South Dakota", value: "SD" },
    { label: "Tennessee", value: "TN" },
    { label: "Texas", value: "TX" },
    { label: "Utah", value: "UT" },
    { label: "Vermont", value: "VT" },
    { label: "Virgin Islands", value: "VI" },
    { label: "Virginia", value: "VA" },
    { label: "Washington", value: "WA" },
    { label: "West Virginia", value: "WV" },
    { label: "Wisconsin", value: "WI" },
    { label: "Wyoming", value: "WY" },
  ];

  export const ORDER_STATUS_OPEN = 'Open';
  // Ship to Clinic terminal state (kit delivered to the clinic, awaiting submission).
  export const ORDER_STATUS_DELIVERED = 'Delivered';
  // Shared outbound/return-leg vocabulary - both the outbound leg (Ship to Clinic, before
  // delivery) and the return leg (graduated Ship to Patient, once delivered) use these same
  // three labels, since a carrier shipment goes through the same real-world states regardless
  // of direction - see UpsTrackingService.CheckOrderTrackingCoreAsync.
  export const ORDER_STATUS_LABEL_CREATED = 'Label Created';
  export const ORDER_STATUS_IN_TRANSIT = 'In Transit';
  export const ORDER_STATUS_DELAYED = 'Delayed';
  // Ship to Patient return-leg vocabulary (set on both the NuviaOrder and the graduated Lab
  // Order once the outbound kit is delivered - see UpsTrackingService.CheckOrderTrackingCoreAsync).
  export const ORDER_STATUS_AWAITING_RETURN = 'Awaiting Return Shipment';
  // Terminal for Ship to Patient - the returned specimen is confirmed delivered to the lab.
  export const ORDER_STATUS_LAB = 'In Lab';
  export const ORDER_STATUS_COMPLETED = 'Completed';
  export const ORDER_STATUS_CANCELED_VOIDED = 'Canceled Voided';
  export const ORDER_STATUS_CANCELED_DAMAGED = 'Canceled Damaged';
  export const ORDER_STATUS_CANCELED_UNUSEABLE = 'Canceled Unuseable';

  export const ORDER_SAMPLE_STATUS_READY = 'Results Ready';
  export const ORDER_SAMPLE_STATUS_LAB_REVIEW = 'Lab Review';

  export const ORDER_STATUS_LIST: ISelectItem[] = [
    { label: ORDER_STATUS_OPEN, value: ORDER_STATUS_OPEN },
    { label: ORDER_STATUS_LABEL_CREATED, value: ORDER_STATUS_LABEL_CREATED },
    { label: ORDER_STATUS_IN_TRANSIT, value: ORDER_STATUS_IN_TRANSIT },
    { label: ORDER_STATUS_DELAYED, value: ORDER_STATUS_DELAYED },
    { label: ORDER_STATUS_DELIVERED, value: ORDER_STATUS_DELIVERED },
    { label: ORDER_STATUS_AWAITING_RETURN, value: ORDER_STATUS_AWAITING_RETURN },
    { label: ORDER_STATUS_LAB, value: ORDER_STATUS_LAB },
    { label: ORDER_STATUS_COMPLETED, value: ORDER_STATUS_COMPLETED },
    { label: ORDER_STATUS_CANCELED_VOIDED, value: ORDER_STATUS_CANCELED_VOIDED },
    { label: ORDER_STATUS_CANCELED_DAMAGED, value: ORDER_STATUS_CANCELED_DAMAGED },
    { label: ORDER_STATUS_CANCELED_UNUSEABLE, value: ORDER_STATUS_CANCELED_UNUSEABLE },
  ];

  export const PROVIDER_TYPE_PHYSICIAN = 'Physician';
  export const PROVIDER_TYPE_NURSE_PRACTITIONER = 'Nurse Practitioner';
  export const PROVIDER_TYPE_PHYSICIAN_ASSISTANT = 'Physician Assistant';
  export const PROVIDER_TYPE_DENTIST_DDS = 'Dentist DDS';
  export const PROVIDER_TYPE_DENTIST_DMD = 'Dentist DMD';
  export const PROVIDER_TYPE_PHARMICIST = 'Pharmacist';
  export const PROVIDER_TYPE_OTHER = 'Other';
  export const PROVIDER_TYPE_NURSE = 'Nurse';
  export const PROVIDER_TYPE_DENTAL_HYGIENIST = 'Dental Hygienist';

  export const PROVIDER_TYPES_LIST: ISelectItem[] = [
    { label: PROVIDER_TYPE_DENTIST_DDS, value: PROVIDER_TYPE_DENTIST_DDS },
    { label: PROVIDER_TYPE_DENTIST_DMD, value: PROVIDER_TYPE_DENTIST_DMD },
    { label: PROVIDER_TYPE_NURSE_PRACTITIONER, value: PROVIDER_TYPE_NURSE_PRACTITIONER },
    { label: PROVIDER_TYPE_PHYSICIAN, value: PROVIDER_TYPE_PHYSICIAN },
    { label: PROVIDER_TYPE_PHYSICIAN_ASSISTANT, value: PROVIDER_TYPE_PHYSICIAN_ASSISTANT },
    { label: PROVIDER_TYPE_PHARMICIST, value: PROVIDER_TYPE_PHARMICIST },
    { label: PROVIDER_TYPE_OTHER, value: PROVIDER_TYPE_OTHER },
    { label: PROVIDER_TYPE_NURSE, value: PROVIDER_TYPE_NURSE },
    { label: PROVIDER_TYPE_DENTAL_HYGIENIST, value: PROVIDER_TYPE_DENTAL_HYGIENIST },
  ];

  // Provider types that are individually credentialed and carry their own NPI.
  // Excludes Other, Nurse, and Dental Hygienist, which typically practice under
  // another provider's NPI rather than holding an individual one.
  export const PROVIDER_TYPES_REQUIRING_NPI: string[] = [
    PROVIDER_TYPE_DENTIST_DDS,
    PROVIDER_TYPE_DENTIST_DMD,
    PROVIDER_TYPE_NURSE_PRACTITIONER,
    PROVIDER_TYPE_PHYSICIAN,
    PROVIDER_TYPE_PHYSICIAN_ASSISTANT,
    PROVIDER_TYPE_PHARMICIST,
  ];

export const ORGANIZATION_TYPE_MEDICAL = 'Medical';
export const ORGANIZATION_TYPE_DENTAL = 'Dental';
export const ORGANIZATION_TYPE_PHARMACY = 'Pharmacy';
export const ORGANIZATION_TYPE_BUSINESS = 'Business';
export const ORGANIZATION_TYPE_OTHER = 'Other';

export const ORGANIZATION_BILLING_OPTION_LIST: ISelectItem[] = [
    { label: 'Not Active', value: 'Not Active' },
    { label: 'Pre-purchase Test Subscriptions', value: 'Pre-purchase Test Subscriptions' },
    { label: 'Automated Billing (Weekly)', value: 'Automated Billing (Weekly)' },
]

export const ORGANIZATION_TYPE_LIST: ISelectItem[] = [
    { label: ORGANIZATION_TYPE_BUSINESS, value: ORGANIZATION_TYPE_BUSINESS },
    { label: ORGANIZATION_TYPE_DENTAL, value: ORGANIZATION_TYPE_DENTAL },
    { label: ORGANIZATION_TYPE_MEDICAL, value: ORGANIZATION_TYPE_MEDICAL },
    { label: ORGANIZATION_TYPE_PHARMACY, value: ORGANIZATION_TYPE_PHARMACY },
    { label: ORGANIZATION_TYPE_OTHER, value: ORGANIZATION_TYPE_OTHER },
];

export const ORGANIZATION_PAYMENT_TYPE_LIST: ISelectItem[] = [
    { label: '', value: '' },
    { label: 'CC', value: 'CC' },
    { label: 'ACH', value: 'ACH' },
]

// Enterprise/billing feature - which Stripe payment method Pre-Buy charges for this org should
// resolve to. Only meaningful when a Enterprise is also selected.
export const ORGANIZATION_PAYMENT_SOURCE_LIST: ISelectItem[] = [
    { label: 'This Organization', value: 'Organization' },
    { label: 'Enterprise', value: 'Enterprise' },
]

export const ORGANIZATION_ACH_ACCOUNT_TYPE_LIST: ISelectItem[] = [
    { label: 'BUSINESS CHECKING', value: 'BUSINESS CHECKING' },
    { label: 'BUSINESS SAVINGS', value: 'BUSINESS SAVINGS' },
    { label: 'PERSONAL CHECKING', value: 'PERSONAL CHECKING' },
    { label: 'PERSONAL SAVINGS', value: 'PERSONAL SAVINGS' },
]

export const CREDIT_PURCHASE_QTY_OPTION_LIST: ISelectItem[] = [
    { label: '12', value: '12' },
    { label: '24', value: '24' },
];

export const ORGANIZATION_TEST_CREDIT_SELECTION_LIST: ISelectItem[] = [
    { label: '0', value: '0' },
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4', value: '4' },
    { label: '5', value: '5' },
    { label: '6', value: '6' },
    { label: '7', value: '7' },
    { label: '8', value: '8' },
    { label: '9', value: '9' },
    { label: '10', value: '10' },
    { label: '11', value: '11' },
    { label: '12', value: '12' },
    { label: '13', value: '13' },
    { label: '14', value: '14' },
    { label: '15', value: '15' },
    { label: '16', value: '16' },
    { label: '17', value: '17' },
    { label: '18', value: '18' },
    { label: '19', value: '19' },
    { label: '20', value: '20' },
    { label: '21', value: '21' },
    { label: '22', value: '22' },
    { label: '23', value: '23' },
    { label: '24', value: '24' },
    { label: '25', value: '25' },
    { label: '26', value: '26' },
    { label: '27', value: '27' },
    { label: '28', value: '28' },
    { label: '29', value: '29' },
    { label: '30', value: '30' },
    { label: '31', value: '31' },
    { label: '32', value: '32' },
    { label: '33', value: '33' },
    { label: '34', value: '34' },
    { label: '35', value: '35' },
    { label: '36', value: '36' },
    { label: '37', value: '37' },
    { label: '38', value: '38' },
    { label: '39', value: '39' },
    { label: '40', value: '40' },
];

export const REPURCHASE_THRESHOLD_QTY_OPTION_LIST: ISelectItem[] = [
    { label: '5', value: '5' },
    { label: '10', value: '10' },
];

export const CC_EXP_MONTH_LIST: ISelectItem[] = [
    { label: 'Jan', value: '01' },
    { label: 'Feb', value: '02' },
    { label: 'Mar', value: '03' },
    { label: 'Apr', value: '04' },
    { label: 'May', value: '05' },
    { label: 'June', value: '06' },
    { label: 'July', value: '07' },
    { label: 'Aug', value: '08' },
    { label: 'Sept', value: '09' },
    { label: 'Oct', value: '10' },
    { label: 'Nov', value: '11' },
    { label: 'Dec', value: '12' },
];

export const CC_EXP_YEAR_LIST: ISelectItem[] = [
    { label: '2025', value: '25' },
    { label: '2026', value: '26' },
    { label: '2027', value: '27' },
    { label: '2028', value: '28' },
    { label: '2029', value: '29' },
    { label: '2030', value: '30' },
    { label: '2031', value: '31' },
    { label: '2032', value: '32' },
    { label: '2033', value: '33' },
];

export const TEST_LOCATIONS_LIST: ISelectItem[] = [
    { label: 'In Clinic', value: 'In Clinic' },
    { label: 'At Home', value: 'At Home' },
];
